/* eslint-disable react/no-danger */
import React, { useState } from 'react';
import _ from 'lodash';
import { motion } from 'framer-motion';
import { Link } from 'gatsby';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import BoxedContainer from './layouts/BoxedContainer';
import useMediaQuery from '../hooks/useMediaQuery';

function DownloadItem({ data, sId, index }) {
  const title = _.get(data, 'content.title', '');
  const text = _.get(data, 'content.text', '');
  const bulletPoints = _.get(data, 'content.bulletPoints', []);
  const cta = _.get(data, 'content.cta', {});
  const image = _.get(data, 'content.image', {});
  const imageMobile = _.get(data, 'content.imageMobile', {}) 
  ? _.get(data, 'content.imageMobile', {}) 
  : image;
  const [toggle, setToggle] = useState(false);
  const isDesktop = useMediaQuery('(min-width: 640px)');

  return (
    <div className="flex flex-col border bg-white">
      {_.isEmpty(image) || (
        <LazyLoadImage
          src={image.url}
          alt={image.alt}
          effect="fade"
          wrapperClassName="!hidden sm:!inline-block"
          className="w-full aspect-[370/300] object-cover"
        />
      )}
      {_.isEmpty(imageMobile) || (
        <LazyLoadImage
          src={imageMobile.url}
          alt={imageMobile.alt}
          effect="fade"
          wrapperClassName="sm:!hidden"
          className="w-full aspect-[273/144] object-cover"
        />
      )}
      <motion.div
        initial={{ opacity: 0, y: 50 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.5 }}
        viewport={{ once: true }}
        className="w-full px-4 py-5 xl:p-7.5 gap-5 md:gap-10 flex flex-col grow">
        {isDesktop ? (
          <div className="flex flex-col gap-5">
            <h2 className="text-clamp14to25 leading-1.5 m-0 text-[#58595B] !font-LibreBaskervilleRegular">{title}</h2>
            {_.isEmpty(text) || (
            <p className="text-clamp12to16 leading-1.5 m-0 text-[#4A4A4A] font-DINRegular">
              {text}
            </p>
            )}
          </div>
        ) : (
          <div className="w-full gap-3 flex flex-col grow">
            <button
              type="button"
              className="w-full flex justify-between items-center gap-4"
              onClick={() => setToggle(!toggle)}>
              <h2 className="text-clamp14to25 leading-1.5 m-0 text-[#58595B] !font-LibreBaskervilleRegular">{title}</h2>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="7"
                viewBox="0 0 12 7"
                fill="none"
                data-toggle={toggle}
                className="transition data-[toggle=false]:rotate-180">
                <path
                  d="M11 6L6 1L1 6"
                  stroke="#58595B"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p
              data-toggle={toggle}
              className="text-clamp12to16 leading-1.5 m-0 data-[toggle=false]:hidden text-[#4a4a4a]
              font-URWDINRegular">
              {text}
            </p>
          </div>
        )}

        <ul
          data-toggle={isDesktop || toggle}
          className="flex flex-col grow gap-4 lg:gap-6 data-[toggle=false]:hidden">
          {_.map(bulletPoints, (point, key1) => (
            <li
              key={key1}
              className="flex gap-3 md:gap-4 xl:gap-5 text-clamp13to16 child:ltr:font-URWDINRegular 
              child:rtl:font-GESSTwoDWTCLight text-[#000]">
              <svg
                className="w-5 min-w-5 md:w-[25px] h-4 md:h-7 md:min-w-[25px] relative lg:top-[-4px]"
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="27"
                viewBox="0 0 28 27"
                fill="none">
                <path
                  d="M25.375 13.5C25.375 20.375 19.9063 26 13.1875 26C6.46875 26 1 20.375 1 13.5C1
                  6.625 6.46875 1 13.1875 1C15.6875 1 18.1875 1.78125 20.2188 3.26562C20.4531
                  3.42188 20.5312 3.8125 20.375 4.04688C20.2188 4.28125 19.8281 4.35938 19.5938
                  4.125C17.7188 2.79688 15.5313 2.09375 13.1875 2.09375C7.09375 2.09375 2.09375 7.25
                  2.09375 13.5C2.09375 19.75 7.09375 24.9062 13.1875 24.9062C19.2813 24.9062 24.2813
                  19.75 24.2813 13.5C24.2813 13.1875 24.5156 12.9531 24.8281 12.9531C25.1406 12.9531
                  25.375 13.1875 25.375 13.5Z"
                  fill="#CE445B"
                  stroke="#CE445B"
                />
                <path
                  d="M26.625 3.88989C26.3906 3.65552 26 3.65552 25.8438 3.88989L13.8906
                  16.1555L8.8125 10.9211C8.57812 10.6868 8.26563 10.6868 8.03125 10.9211C7.79687
                  11.1555 7.79687 11.468 8.03125 11.7024L13.5 17.3274C13.5781 17.4055 13.7344
                  17.4836 13.8906 17.4836C14.0469 17.4836 14.2031 17.4055 14.2813 17.3274L26.625
                  4.67114C26.8594 4.51489 26.8594 4.12427 26.625 3.88989Z"
                  fill="#CE445B"
                  stroke="#CE445B"
                />
              </svg>
              <span dangerouslySetInnerHTML={{__html: point}} className='leading-[1.4]'/>
            </li>
          ))}
        </ul>
        {_.isEmpty(cta) || (
          <Link
            data-title={title}
            to={cta.url}
            target={cta.target}
            data-toggle={isDesktop || toggle}
            className='card-cta w-fit md:w-full flex justify-center items-center 
            rounded-[48px] bg-[#CE445B] hover:bg-[#B53B50] text-white 
            uppercase ltr:font-URWDINBold rtl:font-GESSTwoDWTCMedium ltr:tracking-[0.96px] 
            leading-1.5 md:leading-1.25
            min-h-8 md:h-[51px] text-center px-12 md:px-7 py-2
            rtl:text-[12px] ltr:text-[11px] rtl:md:text-[16px] ltr:md:text-[15px] 
            rtl:lg:text-[18px]
            md:py-5 rtl:pt-[10px] ltr:pt-[12px] ltr:md:pt-6 rtl:md:pt-5  
            data-[toggle=false]:hidden'>
            {cta.name}
          </Link>
        )}
      </motion.div>
    </div>
  );
}

function FreeZoneFourCardsWithList({ id, data }) {
  const title = _.get(data, 'title', '');
  const text = _.get(data, 'text', '');
  const cardItems = _.get(data, 'cardItems', []);
  const bgColor = `#${_.get(data, 'bgColor', 'ffffff')}`;
  const sectionID = _.get(data, 'sectionID', '');

  return (
    <section
      id={sectionID === null ? id : sectionID}
      className="relative"
      style={{ backgroundColor: bgColor }}>
      <BoxedContainer variant="lg">
        <div className="w-full py-12 md:py-16 lg:py-20 xl:py-25 flex flex-col items-center">
          <div className="flex flex-col items-center gap-5">
            {title && (
              <motion.div
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.4 }}
                viewport={{ once: true }}
                className="child:text-clamp20to40 child:ltr:!font-URWDINMedium 
                child:rtl:!font-GESSTwoDWTCMedium child:leading-[1.3]
                last:child:!m-0 child:text-center child:text-[#231F20]"
                dangerouslySetInnerHTML={{ __html: title }}
              />
            )}
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              viewport={{ once: true }}
              className="py-px bg-red w-10 lg:w-12.5"
            />
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              viewport={{ once: true }}
              className="child:text-clamp12to18 child:ltr:!font-URWDINRegular 
              child:rtl:!font-GESSTwoDWTCLight child:leading-1.4
              last:child:!m-0 child:text-center child:text-[#4A4A4A] md:pt-[10px] max-w-[1170px]"
              dangerouslySetInnerHTML={{ __html: text }}
            />
          </div>
          {/* <div
            className="w-full grid sm:grid-cols-2 lg:grid-cols-3 1xl:grid-cols-4
            gap-y-[30px] gap-5 lg:gap-[30px] mt-7.5 lg:mt-12.5">
            {_.map(cardItems, (item, index) => (
              <DownloadItem index={index} key={index} data={item} sId={sectionID} />
            ))}
          </div> */}
          <div
            className={`w-full  
              ${cardItems.length > 4 && cardItems.length < 8 
                ? 'max-w-[1170px] flex flex-wrap  justify-center [&>div]:sm:w-[calc(100%/2-30px)] [&>div]:lg:w-[calc(100%/3-30px)]' 
                : 'grid sm:grid-cols-2 lg:grid-cols-3 1xl:grid-cols-4'} 
              gap-y-[30px] gap-5 lg:gap-[30px] mt-7.5 lg:mt-12.5`}>
            {_.map(cardItems, (item, index) => (
              <DownloadItem index={index} key={index} data={item} sId={sectionID} />
            ))}
          </div>
        </div>
      </BoxedContainer>
    </section>
  );
}

export default FreeZoneFourCardsWithList;
